import React from "react"; 
import {
  Card,
  CardContent,
  Typography,
  Button,
} from "@mui/material";
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Edit as EditIcon,
  Block as BlockIcon,
  CheckCircle as EnableIcon,
  Delete as DeleteIcon
} from "@mui/icons-material";
import { CoOwner } from "../../../validation/business.validation";
import ConfirmDeleteDialog from "../../../common/confirmDeleteAlert";
import ConfirmBlockDialog from "../../../common/confirmBlockAlert";
import {formatPhoneNumber} from "../../../helper/phoneFormatter.helper";

interface Props {
  coOwners: CoOwner[];
  onDelete: (emp: CoOwner) => void;
  onEdit: (emp: CoOwner) => void;
  onBlock: (emp: CoOwner) => void;
}

const CoOwnerCardView: React.FC<Props> = ({
  coOwners,
  onDelete,
  onEdit,
  onBlock,
}) => {
  const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false);
  const [openStatusDialog, setOpenStatusDialog] = React.useState(false);
  const [selectedCoOwner, setSelectedCoOwner] = React.useState<CoOwner | null>(
    null
  );

  const handleDeleteConfirm = () => {
    if (selectedCoOwner) {
      onDelete(selectedCoOwner);
    }
    setOpenDeleteDialog(false);
    setSelectedCoOwner(null);
  };

  const handleStatusConfirm = () => {
    if (selectedCoOwner) {
      onBlock(selectedCoOwner);
    }
    setOpenStatusDialog(false);
    setSelectedCoOwner(null);
  };

  const openDeactivateDialog = (coOwner: CoOwner) => {
    setSelectedCoOwner(coOwner);
    setOpenStatusDialog(true);
  };

  const openDeleteDialogHandler = (coOwner: CoOwner) => {
    setSelectedCoOwner(coOwner);
    setOpenDeleteDialog(true);
  };

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {coOwners.map((coOwner, index) => {
          const isActive = coOwner.isActive;
          return (
            <Card
              key={index}
              className={`rounded-2xl border shadow border-gray-200`}
            >
              <CardContent
                className={`flex flex-col items-center text-center px-4 py-6 gap-4 ${
                  !isActive ? "bg-gray-200" : ""
                }`}
              >
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center shadow">
                  <PersonIcon className="text-gray-400 text-6xl" />
                </div>

                <Typography
                  variant="h6"
                  className="font-semibold text-gray-900"
                >
                  {`${(coOwner as any).firstName || ''} ${(coOwner as any).middleName || ''} ${(coOwner as any).lastName || ''}`.trim() || (coOwner as any).name || "-"}
                </Typography>

                <div className="text-sm text-gray-700 space-y-2 text-left w-full">
                  <div className="flex items-center gap-2">
                    <EmailIcon fontSize="small" />
                    <span>
                      <strong>Email:</strong> {coOwner.email || "-"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <PhoneIcon fontSize="small" />
                    <span>
                      <strong>Phone:</strong> {coOwner.phone ? formatPhoneNumber(coOwner.phone) : "-"}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 w-full mt-4">
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<EditIcon />}
                    color="secondary"
                    fullWidth
                    sx={{ borderRadius: 2, py: 0.5, fontWeight: 600 }}
                    onClick={() => onEdit(coOwner)}
                    className="md:col-span-2"
                  >
                    Edit
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={isActive ? <BlockIcon /> : <EnableIcon />}
                    color={isActive ? "warning" : "success"}
                    fullWidth
                    sx={{ borderRadius: 2, py: 0.5, fontWeight: 600 }}
                    onClick={() => openDeactivateDialog(coOwner)}
                  >
                    {isActive ? "Deactivate" : "Enable"}
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<DeleteIcon />}
                    color="error"
                    fullWidth
                    sx={{ borderRadius: 2, py: 0.5, fontWeight: 600 }}
                    onClick={() => openDeleteDialogHandler(coOwner)}
                  >
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Delete Confirm */}
      <ConfirmDeleteDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={handleDeleteConfirm}
        itemName={selectedCoOwner?.name || ""}
      />

      {/* Status Change Confirm */}
      <ConfirmBlockDialog
        open={openStatusDialog}
        isActive={!!selectedCoOwner?.isActive}
        onClose={() => setOpenStatusDialog(false)}
        onConfirm={handleStatusConfirm}
        itemName={selectedCoOwner?.name}
      />
    </>
  );
};

export default CoOwnerCardView;
